import {
  For,
  Show,
  createEffect,
  createResource,
  createSignal,
  type JSX,
} from "solid-js";
import { A, useParams } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import ChallengeSettingsCard from "../components/ChallengeSettingsCard";
import CompanyFeaturesCard from "../components/CompanyFeaturesCard";
import PageHeader from "../components/PageHeader";
import {
  api,
  failureSentence,
  type OperatorApiPresetView,
  type OperatorApiReadinessFinding,
  type OperatorApiReadinessReport,
  type OperatorApiScope,
  type OperatorRateLimitTier,
  type ProvisionedOperatorCredentials,
  type RotatedSigningSecret,
  type WebhookDeliveryStatus,
  type WebhookEventType,
  type WebhookSubscriptionSetting,
} from "../lib/api";
import { parseAllowedOriginsInput } from "../lib/operatorInputs";
import {
  formatOperatorApiScopes,
  operatorApiScopesForSelection,
} from "../lib/operatorScopes";
import {
  formatWebhookEventTypes,
  formatWebhookSubscription,
} from "../lib/operatorWebhooks";
import {
  NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND,
  NOCTANT_HISTORICAL_BACKTEST_LAUNCH_PROOF_KIND,
  NOCTANT_HISTORICAL_REPLAY_LAUNCH_PROOF_KIND,
  initialSmokeProofForm,
  normalizeSmokeProofRecord,
  type SmokeProofFormField,
  type SmokeProofFormState,
} from "../lib/operatorSmokeProof";
import { canMutate, useSession } from "../lib/session";
import { formatTimestamp } from "../lib/timestamp";

const OPERATOR_RATE_LIMIT_TIERS = [
  "standard",
  "pro",
  "enterprise",
  "unlimited",
] as const;
const ONBOARDING_SIGNOFF_KIND = "private_beta_operator_onboarding" as const;
const ONBOARDING_SIGNOFF_STATUSES = [
  "approved",
  "approved_with_limitations",
] as const;
type OnboardingSignoffStatus = (typeof ONBOARDING_SIGNOFF_STATUSES)[number];

export default function OperatorDetail() {
  const params = useParams<{ operatorId: string }>();
  const session = useSession();

  const [op, { refetch }] = createResource(
    () => params.operatorId,
    (id) => api.getOperator(id),
  );
  const [readiness, { refetch: refetchReadiness }] = createResource(
    () => params.operatorId,
    (id) => api.getOperatorApiReadiness(id),
  );
  const [scopeOptions] = createResource(async () => {
    const response = await api.listOperatorApiScopes();
    return response.operator_api_scopes;
  });
  const [presetOptions] = createResource(async () => {
    const response = await api.listOperatorApiPresets();
    return response.operator_api_presets;
  });
  const [webhookEventOptions] = createResource(async () => {
    const response = await api.listWebhookEventTypes();
    return response.webhook_event_types;
  });

  // The chosen preset's token (the options' value); "" is none.
  const [selectedPresetKey, setSelectedPresetKey] = createSignal("");
  const [selectedRateLimitTier, setSelectedRateLimitTier] =
    createSignal<OperatorRateLimitTier>("standard");
  const [webhookInput, setWebhookInput] = createSignal("");
  const [originsInput, setOriginsInput] = createSignal("");
  const [webhookEventMode, setWebhookEventMode] = createSignal<
    "default" | "explicit"
  >("default");
  const [selectedWebhookEventTypes, setSelectedWebhookEventTypes] =
    createSignal<Set<WebhookEventType>>(new Set());
  const [selectedScopes, setSelectedScopes] = createSignal<
    Set<OperatorApiScope>
  >(new Set());

  createEffect(() => {
    const current = op();
    if (!current) return;

    setWebhookInput(current.webhook_url ?? "");
    setSelectedRateLimitTier(current.rate_limit_tier);
    setOriginsInput(current.allowed_origins.join("\n"));
    setWebhookEventMode(current.webhook_subscription.mode);
    setSelectedWebhookEventTypes(
      new Set(current.webhook_subscription.event_types),
    );
    setSelectedScopes(
      new Set(operatorApiScopesForSelection(current.operator_api_scopes)),
    );
  });

  const canEdit = () => {
    const admin = session.admin();
    return admin ? canMutate(admin.role) : false;
  };

  const selectedPreset = () => {
    const presetKey = selectedPresetKey();
    if (!presetKey) return undefined;
    return presetOptions()?.find((option) => option.key_token === presetKey);
  };

  function refreshOperatorState() {
    refetch();
    refetchReadiness();
  }

  // ------ Rate-limit tier update ------
  const [savingRateLimitTier, setSavingRateLimitTier] = createSignal(false);
  const [rateLimitTierError, setRateLimitTierError] = createSignal<string | null>(
    null,
  );
  const [rateLimitTierSaved, setRateLimitTierSaved] = createSignal(false);

  async function onSaveRateLimitTier(ev: Event) {
    ev.preventDefault();
    const rateLimitTier = selectedRateLimitTier();
    if (
      rateLimitTier === "unlimited" &&
      !confirm(
        "Move this operator to unlimited rate limits? Readiness will continue to warn until the beta record explains why.",
      )
    ) {
      return;
    }

    setSavingRateLimitTier(true);
    setRateLimitTierError(null);
    setRateLimitTierSaved(false);
    try {
      await api.updateRateLimitTier(params.operatorId, rateLimitTier);
      setRateLimitTierSaved(true);
      refreshOperatorState();
    } catch (e) {
      setRateLimitTierError(failureSentence(e));
    } finally {
      setSavingRateLimitTier(false);
    }
  }

  // ------ Operator API preset application ------
  const [savingPreset, setSavingPreset] = createSignal(false);
  const [presetError, setPresetError] = createSignal<string | null>(null);
  const [presetSaved, setPresetSaved] = createSignal(false);

  async function onApplyPreset(ev: Event) {
    ev.preventDefault();
    const presetKey = selectedPresetKey();
    const preset = presetOptions()?.find((option) => option.key_token === presetKey);
    if (!presetKey || !preset) {
      setPresetError("select an operator_api preset");
      setPresetSaved(false);
      return;
    }
    if (!preset.can_apply) {
      setPresetError("selected preset is a future surface and cannot be applied");
      setPresetSaved(false);
      return;
    }
    if (
      !confirm(
        `Apply ${preset.label}? This replaces Operator API scopes and webhook event filters.`,
      )
    ) {
      return;
    }

    setSavingPreset(true);
    setPresetError(null);
    setPresetSaved(false);
    try {
      await api.applyOperatorApiPreset(params.operatorId, preset.key);
      setPresetSaved(true);
      refreshOperatorState();
    } catch (e) {
      setPresetError(failureSentence(e));
    } finally {
      setSavingPreset(false);
    }
  }

  // ------ First credential provisioning ------
  const [provisioning, setProvisioning] = createSignal(false);
  const [provisioned, setProvisioned] =
    createSignal<ProvisionedOperatorCredentials | null>(null);
  const [provisionError, setProvisionError] = createSignal<string | null>(null);

  async function onProvision() {
    if (
      !confirm(
        "Provision this operator's first API key and webhook signing secret?",
      )
    ) {
      return;
    }
    setProvisioning(true);
    setProvisionError(null);
    try {
      const result = await api.provisionCredentials(params.operatorId);
      setProvisioned(result);
      refreshOperatorState();
    } catch (e) {
      setProvisionError(failureSentence(e));
    } finally {
      setProvisioning(false);
    }
  }

  // ------ API key rotation ------
  const [rotating, setRotating] = createSignal(false);
  const [rotated, setRotated] = createSignal<
    { api_key: string; api_key_prefix: string } | null
  >(null);
  const [rotateError, setRotateError] = createSignal<string | null>(null);

  async function onRotate() {
    if (
      !confirm(
        "Rotate this operator's API key? The current key stops working immediately.",
      )
    ) {
      return;
    }
    setRotating(true);
    setRotateError(null);
    try {
      const result = await api.rotateApiKey(params.operatorId);
      setRotated(result);
      refreshOperatorState();
    } catch (e) {
      setRotateError(failureSentence(e));
    } finally {
      setRotating(false);
    }
  }

  // ------ Webhook signing secret rotation ------
  const [secretRotating, setSecretRotating] = createSignal(false);
  const [rotatedSecret, setRotatedSecret] =
    createSignal<RotatedSigningSecret | null>(null);
  const [secretError, setSecretError] = createSignal<string | null>(null);
  const [deliveryResuming, setDeliveryResuming] = createSignal(false);

  async function onRotateSigningSecret() {
    if (
      !confirm(
        "Rotate this operator's webhook signing secret and pause delivery? Update the receiver with the new secret, then resume queued deliveries.",
      )
    ) {
      return;
    }
    setSecretRotating(true);
    setSecretError(null);
    try {
      const result = await api.rotateSigningSecret(params.operatorId);
      setRotatedSecret(result);
      refreshOperatorState();
    } catch (e) {
      setSecretError(failureSentence(e));
    } finally {
      setSecretRotating(false);
    }
  }

  async function onResumeWebhookDelivery(rotationId: string) {
    setDeliveryResuming(true);
    setSecretError(null);
    try {
      await api.resumeWebhookDelivery(params.operatorId, rotationId);
      setRotatedSecret(null);
      refreshOperatorState();
    } catch (e) {
      setSecretError(failureSentence(e));
    } finally {
      setDeliveryResuming(false);
    }
  }

  // ------ Webhook URL update ------
  const [savingHook, setSavingHook] = createSignal(false);
  const [hookError, setHookError] = createSignal<string | null>(null);
  const [hookSaved, setHookSaved] = createSignal(false);

  async function onSaveWebhook(ev: Event) {
    ev.preventDefault();
    setSavingHook(true);
    setHookError(null);
    setHookSaved(false);
    try {
      const next = webhookInput().trim();
      await api.updateWebhookUrl(params.operatorId, next === "" ? null : next);
      setHookSaved(true);
      refreshOperatorState();
    } catch (e) {
      setHookError(failureSentence(e));
    } finally {
      setSavingHook(false);
    }
  }

  // ------ Allowed origins update ------
  const [savingOrigins, setSavingOrigins] = createSignal(false);
  const [originsError, setOriginsError] = createSignal<string | null>(null);
  const [originsSaved, setOriginsSaved] = createSignal(false);

  async function onSaveOrigins(ev: Event) {
    ev.preventDefault();
    setSavingOrigins(true);
    setOriginsError(null);
    setOriginsSaved(false);
    try {
      const origins = parseAllowedOriginsInput(originsInput());
      await api.updateAllowedOrigins(params.operatorId, origins);
      setOriginsSaved(true);
      refreshOperatorState();
    } catch (e) {
      setOriginsError(failureSentence(e));
    } finally {
      setSavingOrigins(false);
    }
  }

  // ------ Webhook event type update ------
  const [savingWebhookEvents, setSavingWebhookEvents] = createSignal(false);
  const [webhookEventsError, setWebhookEventsError] = createSignal<
    string | null
  >(null);
  const [webhookEventsSaved, setWebhookEventsSaved] = createSignal(false);

  function toggleWebhookEventType(eventType: WebhookEventType, checked: boolean) {
    setSelectedWebhookEventTypes((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(eventType);
      } else {
        next.delete(eventType);
      }
      return next;
    });
  }

  function selectAllWebhookEventTypes() {
    setWebhookEventMode("explicit");
    setSelectedWebhookEventTypes(
      new Set(webhookEventOptions()?.map((option) => option.value) ?? []),
    );
  }

  function restoreCurrentWebhookEventTypes() {
    const current = op();
    if (!current) return;
    setWebhookEventMode(current.webhook_subscription.mode);
    setSelectedWebhookEventTypes(
      new Set(current.webhook_subscription.event_types),
    );
  }

  async function onSaveWebhookEventTypes(ev: Event) {
    ev.preventDefault();
    const options = webhookEventOptions() ?? [];
    const eventTypes = options
      .map((option) => option.value)
      .filter((eventType) => selectedWebhookEventTypes().has(eventType));
    const subscription: WebhookSubscriptionSetting =
      webhookEventMode() === "default"
        ? { mode: "default" }
        : { mode: "explicit", event_types: eventTypes };

    if (subscription.mode === "explicit" && eventTypes.length === 0) {
      setWebhookEventsError("select at least one event type");
      setWebhookEventsSaved(false);
      return;
    }

    setSavingWebhookEvents(true);
    setWebhookEventsError(null);
    setWebhookEventsSaved(false);
    try {
      await api.updateWebhookSubscription(params.operatorId, subscription);
      setWebhookEventsSaved(true);
      refreshOperatorState();
    } catch (e) {
      setWebhookEventsError(failureSentence(e));
    } finally {
      setSavingWebhookEvents(false);
    }
  }

  // ------ Operator API scopes update ------
  const [savingScopes, setSavingScopes] = createSignal(false);
  const [scopesError, setScopesError] = createSignal<string | null>(null);
  const [scopesSaved, setScopesSaved] = createSignal(false);

  function toggleScope(scope: OperatorApiScope, checked: boolean) {
    setSelectedScopes((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(scope);
      } else {
        next.delete(scope);
      }
      return next;
    });
  }

  function selectAllScopes() {
    setSelectedScopes(
      new Set(scopeOptions()?.map((option) => option.value) ?? []),
    );
  }

  function restoreCurrentScopes() {
    const current = op();
    if (!current) return;
    setSelectedScopes(
      new Set(operatorApiScopesForSelection(current.operator_api_scopes)),
    );
  }

  async function onSaveScopes(ev: Event) {
    ev.preventDefault();
    const options = scopeOptions() ?? [];
    const nextScopes = options
      .map((option) => option.value)
      .filter((scope) => selectedScopes().has(scope));

    if (nextScopes.length === 0) {
      setScopesError("operator_api_scopes must contain at least one scope");
      setScopesSaved(false);
      return;
    }

    setSavingScopes(true);
    setScopesError(null);
    setScopesSaved(false);
    try {
      await api.updateOperatorApiScopes(params.operatorId, nextScopes);
      setScopesSaved(true);
      refreshOperatorState();
    } catch (e) {
      setScopesError(failureSentence(e));
    } finally {
      setSavingScopes(false);
    }
  }

  // ------ Smoke proof attachment ------
  const [smokeProofForm, setSmokeProofForm] = createSignal(
    initialSmokeProofForm(),
  );
  const [recordingSmokeProof, setRecordingSmokeProof] = createSignal(false);
  const [smokeProofError, setSmokeProofError] = createSignal<string | null>(
    null,
  );
  const [smokeProofSaved, setSmokeProofSaved] = createSignal(false);

  function setSmokeProofField(field: SmokeProofFormField, value: string) {
    setSmokeProofForm((current) => ({ ...current, [field]: value }));
    setSmokeProofSaved(false);
  }

  function resetSmokeProofForm() {
    setSmokeProofForm(initialSmokeProofForm());
    setSmokeProofError(null);
    setSmokeProofSaved(false);
  }

  async function onRecordSmokeProof(ev: Event) {
    ev.preventDefault();
    const current = op();
    if (!current) {
      setSmokeProofError("operator row is still loading");
      setSmokeProofSaved(false);
      return;
    }

    let proof;
    try {
      proof = normalizeSmokeProofRecord(
        current.operator_id,
        current.org_id,
        smokeProofForm(),
      );
    } catch (error) {
      setSmokeProofError(failureSentence(error));
      setSmokeProofSaved(false);
      return;
    }

    setRecordingSmokeProof(true);
    setSmokeProofError(null);
    setSmokeProofSaved(false);
    try {
      await api.recordOperatorSmokeProof(params.operatorId, proof);
      setSmokeProofSaved(true);
      refetchReadiness();
    } catch (e) {
      setSmokeProofError(failureSentence(e));
    } finally {
      setRecordingSmokeProof(false);
    }
  }

  // ------ Onboarding signoff attachment ------
  const [signoffStatus, setSignoffStatus] =
    createSignal<OnboardingSignoffStatus>("approved");
  const [signoffOwner, setSignoffOwner] = createSignal("");
  const [signoffApprovedAt, setSignoffApprovedAt] = createSignal(
    new Date().toISOString(),
  );
  const [signoffLimitationsInput, setSignoffLimitationsInput] =
    createSignal("");
  const [recordingSignoff, setRecordingSignoff] = createSignal(false);
  const [signoffError, setSignoffError] = createSignal<string | null>(null);
  const [signoffSaved, setSignoffSaved] = createSignal(false);

  async function onRecordOnboardingSignoff(ev: Event) {
    ev.preventDefault();
    const current = op();
    const report = readiness();
    const smokeProofId = report?.last_smoke_proof?.proof_id;
    if (!current) {
      setSignoffError("operator row is still loading");
      setSignoffSaved(false);
      return;
    }
    if (!smokeProofId) {
      setSignoffError("record a smoke proof before signoff");
      setSignoffSaved(false);
      return;
    }
    if (signoffStatus() === "approved" && !report.ready) {
      setSignoffError("approved signoff requires ready readiness");
      setSignoffSaved(false);
      return;
    }

    const owner = signoffOwner().trim();
    if (!owner) {
      setSignoffError("owner is required");
      setSignoffSaved(false);
      return;
    }

    const approvedAt = new Date(signoffApprovedAt());
    if (!Number.isFinite(approvedAt.getTime())) {
      setSignoffError("approved_at must be a valid timestamp");
      setSignoffSaved(false);
      return;
    }

    const knownLimitations = signoffLimitationsInput()
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    if (signoffStatus() === "approved" && knownLimitations.length > 0) {
      setSignoffError("approved signoff cannot include known limitations");
      setSignoffSaved(false);
      return;
    }
    if (
      signoffStatus() === "approved_with_limitations" &&
      knownLimitations.length === 0
    ) {
      setSignoffError("approved_with_limitations requires at least one limitation");
      setSignoffSaved(false);
      return;
    }

    setRecordingSignoff(true);
    setSignoffError(null);
    setSignoffSaved(false);
    try {
      await api.recordOperatorOnboardingSignoff(params.operatorId, {
        operator_id: current.operator_id,
        org_id: current.org_id,
        signoff_kind: ONBOARDING_SIGNOFF_KIND,
        status: signoffStatus(),
        owner,
        approved_at: approvedAt.toISOString(),
        smoke_proof_id: smokeProofId,
        known_limitations: knownLimitations,
      });
      setSignoffSaved(true);
      refetchReadiness();
    } catch (e) {
      setSignoffError(failureSentence(e));
    } finally {
      setRecordingSignoff(false);
    }
  }

  return (
    <AsyncResource resource={op}>
      {(o) => (
        <>
          <PageHeader
            title={o().name}
            subtitle={`${o().slug} · ${o().environment}`}
            actions={
              <A class="btn-secondary" href="/operators">
                ← Back
              </A>
            }
          />

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Field
              label="Status"
              value={<span class={statusClass(o().status)}>{o().status}</span>}
            />
            <Field label="Rate limit tier" value={o().rate_limit_tier} />
            <Field label="API key prefix" value={o().api_key_prefix ?? "—"} mono />
            <Field
              label="Webhook enabled"
              value={o().webhook_enabled ? "yes" : "no"}
            />
            <Field label="Contact email" value={o().contact_email ?? "—"} />
            <Field
              label="Allowed origins"
              value={
                o().allowed_origins.length ? o().allowed_origins.join(", ") : "—"
              }
            />
            <Field
              label="Webhook events"
              value={formatWebhookSubscription(o().webhook_subscription)}
              mono
            />
            <Field
              label="Operator API scopes"
              value={formatOperatorApiScopes(o().operator_api_scopes)}
              mono
            />
            <Field label="Created" value={formatTimestamp(o().created_at)} />
            <Field
              label="Activated"
              value={
                <Show when={o().activated_at} fallback="—">
                  {(at) => formatTimestamp(at())}
                </Show>
              }
            />
            <Field label="Updated" value={formatTimestamp(o().updated_at)} />
          </div>

          <div class="card mb-4">
            <div class="card-header gap-3 flex-wrap">
              <h2 class="text-sm font-semibold">Operator API readiness</h2>
              <Show when={readiness()}>
                {(report) => (
                  <span class={readinessClass(report().ready)}>
                    {report().ready ? "ready" : "not ready"}
                  </span>
                )}
              </Show>
            </div>
            <div class="card-body">
              <AsyncResource resource={readiness}>
                {(report) => <ReadinessReport report={report()} />}
              </AsyncResource>
            </div>
          </div>

          <Show when={session.admin()?.role === "noctant_superadmin"}>
            <CompanyFeaturesCard orgId={o().org_id} />
          </Show>

          <ChallengeSettingsCard orgId={o().org_id} canEdit={canEdit()} />

          <Show when={canEdit()}>
            <div class="space-y-4">
              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Rate limit tier</h2>
                </div>
                <div class="card-body">
                  <form class="space-y-3" onSubmit={onSaveRateLimitTier}>
                    <div>
                      <label class="label" for="rate-limit-tier">
                        Tier
                      </label>
                      <select
                        id="rate-limit-tier"
                        class="select"
                        value={selectedRateLimitTier()}
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          if (isOperatorRateLimitTier(value)) {
                            setSelectedRateLimitTier(value);
                            setRateLimitTierSaved(false);
                          }
                        }}
                      >
                        <For each={OPERATOR_RATE_LIMIT_TIERS}>
                          {(tier) => <option value={tier}>{tier}</option>}
                        </For>
                      </select>
                    </div>
                    <SaveState
                      error={rateLimitTierError()}
                      saved={rateLimitTierSaved()}
                    />
                    <button
                      type="submit"
                      class="btn-primary"
                      disabled={savingRateLimitTier()}
                    >
                      {savingRateLimitTier() ? "Saving…" : "Save tier"}
                    </button>
                  </form>
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Operator API preset</h2>
                </div>
                <div class="card-body">
                  <AsyncResource resource={presetOptions}>
                    {(options) => (
                      <form class="space-y-3" onSubmit={onApplyPreset}>
                        <div>
                          <label class="label" for="operator-api-preset">
                            Preset
                          </label>
                          <select
                            id="operator-api-preset"
                            class="select"
                            value={selectedPresetKey()}
                            onChange={(e) =>
                              setSelectedPresetKey(e.currentTarget.value)
                            }
                          >
                            <option value="" disabled>
                              Select preset
                            </option>
                            <For each={options()}>
                              {(option) => (
                                <option
                                  value={option.key_token}
                                  disabled={!option.can_apply}
                                >
                                  {option.label}
                                  {option.can_apply ? "" : " (future)"}
                                </option>
                              )}
                            </For>
                          </select>
                        </div>
                        <Show when={selectedPreset()}>
                          {(preset) => <PresetPreview preset={preset()} />}
                        </Show>
                        <SaveState
                          error={presetError()}
                          saved={presetSaved()}
                        />
                        <button
                          type="submit"
                          class="btn-primary"
                          disabled={
                            savingPreset() || !selectedPreset()?.can_apply
                          }
                        >
                          {savingPreset() ? "Applying…" : "Apply preset"}
                        </button>
                      </form>
                    )}
                  </AsyncResource>
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Credentials</h2>
                  <div class="flex flex-wrap justify-end gap-2">
                    <button
                      class="btn-primary"
                      onClick={onProvision}
                      disabled={provisioning() || o().status !== "pending"}
                    >
                      {provisioning() ? "Provisioning…" : "Provision"}
                    </button>
                    <button
                      class="btn-danger"
                      onClick={onRotate}
                      disabled={rotating() || o().status !== "active"}
                    >
                      {rotating() ? "Rotating…" : "Rotate key"}
                    </button>
                    <button
                      class="btn-danger"
                      onClick={onRotateSigningSecret}
                      disabled={secretRotating() || o().status !== "active"}
                    >
                      {secretRotating() ? "Rotating…" : "Rotate signing secret"}
                    </button>
                  </div>
                </div>
                <div class="card-body space-y-3 text-sm">
                  <p class="text-slate-600">
                    Plaintext credentials are shown once. Store them in the
                    app's secret store before dismissing them.
                  </p>
                  <Show when={provisionError()}>
                    <p class="text-red-600">{provisionError()}</p>
                  </Show>
                  <Show when={rotateError()}>
                    <p class="text-red-600">{rotateError()}</p>
                  </Show>
                  <Show when={secretError()}>
                    <p class="text-red-600">{secretError()}</p>
                  </Show>
                  <Show when={o().webhook_signing_rotation_id}>
                    {(rotationId) => (
                      <div class="space-y-3 rounded-sm border border-amber-300 bg-amber-50 p-3">
                        <p>
                          Webhook delivery is paused. Store the new signing
                          secret and deploy it to every receiver before resuming
                          queued deliveries.
                        </p>
                        <button
                          class="btn-primary"
                          disabled={
                            deliveryResuming() ||
                            secretRotating() ||
                            o().status !== "active"
                          }
                          onClick={() => onResumeWebhookDelivery(rotationId())}
                        >
                          {deliveryResuming()
                            ? "Resuming…"
                            : "Receiver updated — resume delivery"}
                        </button>
                      </div>
                    )}
                  </Show>
                  <Show when={provisioned()}>
                    {(result) => (
                      <div class="space-y-3 border border-amber-300 bg-amber-50 rounded-sm p-3">
                        <div class="text-xs uppercase text-amber-800 font-semibold">
                          New credentials - shown once
                        </div>
                        <SecretBlock label="API key" value={result().api_key} />
                        <SecretBlock
                          label="Webhook signing secret"
                          value={result().webhook_signing_secret}
                        />
                        <div class="flex gap-2">
                          <button
                            class="btn-secondary"
                            onClick={() =>
                              void navigator.clipboard.writeText(
                                `${result().api_key}\n${result().webhook_signing_secret}`,
                              )
                            }
                          >
                            Copy both
                          </button>
                          <button
                            class="btn-secondary"
                            onClick={() => setProvisioned(null)}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                  </Show>
                  <Show when={rotated()}>
                    {(result) => (
                      <div class="space-y-3 border border-amber-300 bg-amber-50 rounded-sm p-3">
                        <div class="text-xs uppercase text-amber-800 font-semibold">
                          New API key - shown once
                        </div>
                        <SecretBlock label="API key" value={result().api_key} />
                        <div class="flex gap-2">
                          <button
                            class="btn-secondary"
                            onClick={() =>
                              void navigator.clipboard.writeText(result().api_key)
                            }
                          >
                            Copy key
                          </button>
                          <button
                            class="btn-secondary"
                            onClick={() => setRotated(null)}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                  </Show>
                  <Show when={rotatedSecret()}>
                    {(result) => (
                      <div class="space-y-3 border border-amber-300 bg-amber-50 rounded-sm p-3">
                        <div class="text-xs uppercase text-amber-800 font-semibold">
                          New signing secret - shown once
                        </div>
                        <SecretBlock
                          label="Webhook signing secret"
                          value={result().webhook_signing_secret}
                        />
                        <div class="flex gap-2">
                          <button
                            class="btn-secondary"
                            onClick={() =>
                              void navigator.clipboard.writeText(
                                result().webhook_signing_secret,
                              )
                            }
                          >
                            Copy secret
                          </button>
                          <button
                            class="btn-secondary"
                            onClick={() => setRotatedSecret(null)}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                  </Show>
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Webhook URL</h2>
                </div>
                <div class="card-body">
                  <form class="space-y-3" onSubmit={onSaveWebhook}>
                    <div>
                      <label class="label" for="webhook-url">
                        URL
                      </label>
                      <input
                        id="webhook-url"
                        class="input font-mono text-xs"
                        placeholder="https://app.example.com/noctant/webhooks"
                        value={webhookInput()}
                        onInput={(e) => setWebhookInput(e.currentTarget.value)}
                      />
                    </div>
                    <SaveState error={hookError()} saved={hookSaved()} />
                    <button
                      type="submit"
                      class="btn-primary"
                      disabled={savingHook()}
                    >
                      {savingHook() ? "Saving…" : "Save webhook URL"}
                    </button>
                  </form>
                </div>
              </div>

              <div class="card">
                <div class="card-header">
                  <h2 class="text-sm font-semibold">Allowed launch origins</h2>
                </div>
                <div class="card-body">
                  <form class="space-y-3" onSubmit={onSaveOrigins}>
                    <div>
                      <label class="label" for="allowed-origins">
                        Origins
                      </label>
                      <textarea
                        id="allowed-origins"
                        class="textarea font-mono text-xs min-h-28"
                        value={originsInput()}
                        onInput={(e) => setOriginsInput(e.currentTarget.value)}
                      />
                    </div>
                    <SaveState error={originsError()} saved={originsSaved()} />
                    <button
                      type="submit"
                      class="btn-primary"
                      disabled={savingOrigins()}
                    >
                      {savingOrigins() ? "Saving…" : "Save origins"}
                    </button>
                  </form>
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Webhook events</h2>
                  <div class="flex flex-wrap justify-end gap-2">
                    <button
                      class="btn-secondary"
                      onClick={selectAllWebhookEventTypes}
                    >
                      Select all
                    </button>
                    <button
                      class="btn-secondary"
                      onClick={restoreCurrentWebhookEventTypes}
                    >
                      Restore
                    </button>
                  </div>
                </div>
                <div class="card-body">
                  <AsyncResource resource={webhookEventOptions}>
                    {(options) => (
                      <form class="space-y-3" onSubmit={onSaveWebhookEventTypes}>
                        <div class="flex flex-wrap gap-4 text-sm">
                          <label class="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="webhook-event-mode"
                              checked={webhookEventMode() === "default"}
                              onChange={() => setWebhookEventMode("default")}
                            />
                            <span>Default: every event (follows new events)</span>
                          </label>
                          <label class="inline-flex items-center gap-2">
                            <input
                              type="radio"
                              name="webhook-event-mode"
                              checked={webhookEventMode() === "explicit"}
                              onChange={() => setWebhookEventMode("explicit")}
                            />
                            <span>Selected events</span>
                          </label>
                        </div>
                        <Show when={webhookEventMode() === "explicit"}>
                          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                            <For each={options()}>
                              {(option) => (
                                <label class="flex items-start gap-2 rounded-sm border border-slate-200 p-2 text-sm">
                                  <input
                                    type="checkbox"
                                    class="mt-1"
                                    checked={selectedWebhookEventTypes().has(
                                      option.value,
                                    )}
                                    onChange={(e) =>
                                      toggleWebhookEventType(
                                        option.value,
                                        e.currentTarget.checked,
                                      )
                                    }
                                  />
                                  <span>
                                    <span class="block font-medium">
                                      {option.label}
                                    </span>
                                    <span class="block font-mono text-xs text-slate-500">
                                      {option.value}
                                    </span>
                                  </span>
                                </label>
                              )}
                            </For>
                          </div>
                        </Show>
                        <SaveState
                          error={webhookEventsError()}
                          saved={webhookEventsSaved()}
                        />
                        <button
                          type="submit"
                          class="btn-primary"
                          disabled={savingWebhookEvents()}
                        >
                          {savingWebhookEvents() ? "Saving…" : "Save events"}
                        </button>
                      </form>
                    )}
                  </AsyncResource>
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Operator API scopes</h2>
                  <div class="flex flex-wrap justify-end gap-2">
                    <button class="btn-secondary" onClick={selectAllScopes}>
                      Select all
                    </button>
                    <button class="btn-secondary" onClick={restoreCurrentScopes}>
                      Restore
                    </button>
                  </div>
                </div>
                <div class="card-body">
                  <AsyncResource resource={scopeOptions}>
                    {(options) => (
                      <form class="space-y-3" onSubmit={onSaveScopes}>
                        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                          <For each={options()}>
                            {(option) => (
                              <label class="flex items-start gap-2 rounded-sm border border-slate-200 p-2 text-sm">
                                <input
                                  type="checkbox"
                                  class="mt-1"
                                  checked={selectedScopes().has(option.value)}
                                  onChange={(e) =>
                                    toggleScope(
                                      option.value,
                                      e.currentTarget.checked,
                                    )
                                  }
                                />
                                <span>
                                  <span class="block font-medium">
                                    {option.label}
                                  </span>
                                  <span class="block font-mono text-xs text-slate-500">
                                    {option.value}
                                  </span>
                                </span>
                              </label>
                            )}
                          </For>
                        </div>
                        <SaveState error={scopesError()} saved={scopesSaved()} />
                        <button
                          type="submit"
                          class="btn-primary"
                          disabled={savingScopes()}
                        >
                          {savingScopes() ? "Saving…" : "Save scopes"}
                        </button>
                      </form>
                    )}
                  </AsyncResource>
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Smoke proof</h2>
                  <button class="btn-secondary" onClick={resetSmokeProofForm}>
                    Reset
                  </button>
                </div>
                <div class="card-body">
                  <SmokeProofEntryForm
                    form={smokeProofForm()}
                    recording={recordingSmokeProof()}
                    error={smokeProofError()}
                    saved={smokeProofSaved()}
                    onField={setSmokeProofField}
                    onSubmit={onRecordSmokeProof}
                  />
                </div>
              </div>

              <div class="card">
                <div class="card-header gap-3 flex-wrap">
                  <h2 class="text-sm font-semibold">Onboarding signoff</h2>
                </div>
                <div class="card-body">
                  <form class="space-y-3" onSubmit={onRecordOnboardingSignoff}>
                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                      <div>
                        <label class="label" for="signoff-status">
                          Status
                        </label>
                        <select
                          id="signoff-status"
                          class="select"
                          value={signoffStatus()}
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            if (isOnboardingSignoffStatus(value)) {
                              setSignoffStatus(value);
                              setSignoffSaved(false);
                            }
                          }}
                        >
                          <For each={ONBOARDING_SIGNOFF_STATUSES}>
                            {(status) => <option value={status}>{status}</option>}
                          </For>
                        </select>
                      </div>
                      <div>
                        <label class="label" for="signoff-owner">
                          Owner
                        </label>
                        <input
                          id="signoff-owner"
                          class="input"
                          value={signoffOwner()}
                          onInput={(e) => {
                            setSignoffOwner(e.currentTarget.value);
                            setSignoffSaved(false);
                          }}
                        />
                      </div>
                      <div>
                        <label class="label" for="signoff-approved-at">
                          Approved at
                        </label>
                        <input
                          id="signoff-approved-at"
                          class="input font-mono text-xs"
                          value={signoffApprovedAt()}
                          onInput={(e) => {
                            setSignoffApprovedAt(e.currentTarget.value);
                            setSignoffSaved(false);
                          }}
                        />
                      </div>
                      <div>
                        <label class="label" for="signoff-smoke-proof">
                          Smoke proof
                        </label>
                        <input
                          id="signoff-smoke-proof"
                          class="input font-mono text-xs"
                          value={
                            readiness()?.last_smoke_proof?.proof_id ?? "none"
                          }
                          readOnly
                        />
                      </div>
                    </div>
                    <div>
                      <label class="label" for="signoff-limitations">
                        Known limitations
                      </label>
                      <textarea
                        id="signoff-limitations"
                        class="textarea font-mono text-xs min-h-24"
                        value={signoffLimitationsInput()}
                        onInput={(e) => {
                          setSignoffLimitationsInput(e.currentTarget.value);
                          setSignoffSaved(false);
                        }}
                      />
                    </div>
                    <SaveState error={signoffError()} saved={signoffSaved()} />
                    <button
                      type="submit"
                      class="btn-primary"
                      disabled={
                        recordingSignoff() || !readiness()?.last_smoke_proof
                      }
                    >
                      {recordingSignoff() ? "Recording…" : "Record signoff"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </Show>
        </>
      )}
    </AsyncResource>
  );
}

function SecretBlock(props: { label: string; value: string }) {
  return (
    <div>
      <div class="text-xs text-amber-800 font-medium">{props.label}</div>
      <code class="block font-mono text-xs bg-white border border-amber-200 rounded-sm p-2 break-all">
        {props.value}
      </code>
    </div>
  );
}

function isOperatorRateLimitTier(value: string): value is OperatorRateLimitTier {
  return OPERATOR_RATE_LIMIT_TIERS.some((tier) => tier === value);
}

function isOnboardingSignoffStatus(
  value: string,
): value is OnboardingSignoffStatus {
  return ONBOARDING_SIGNOFF_STATUSES.some((status) => status === value);
}

function SaveState(props: { error: string | null; saved: boolean }) {
  return (
    <>
      <Show when={props.error}>
        <p class="text-sm text-red-600">{props.error}</p>
      </Show>
      <Show when={props.saved}>
        <p class="text-sm text-emerald-700">Saved.</p>
      </Show>
    </>
  );
}

function PresetPreview(props: { preset: OperatorApiPresetView }) {
  return (
    <div class="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm space-y-2">
      <div class="text-slate-700">{props.preset.description}</div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div class="text-xs uppercase text-slate-500 font-medium">Scopes</div>
          <div class="font-mono text-xs text-slate-700 wrap-break-word">
            {formatOperatorApiScopes(props.preset.operator_api_scopes)}
          </div>
        </div>
        <div>
          <div class="text-xs uppercase text-slate-500 font-medium">
            Webhook events
          </div>
          <div class="text-xs text-slate-700">Default: every event</div>
        </div>
      </div>
      <Show when={props.preset.notes.length > 0}>
        <ul class="list-disc pl-5 text-xs text-slate-600 space-y-1">
          <For each={props.preset.notes}>{(note) => <li>{note}</li>}</For>
        </ul>
      </Show>
    </div>
  );
}

function SmokeProofEntryForm(props: {
  form: SmokeProofFormState;
  recording: boolean;
  error: string | null;
  saved: boolean;
  onField: (field: SmokeProofFormField, value: string) => void;
  onSubmit: (ev: Event) => void;
}) {
  return (
    <form class="space-y-3" onSubmit={props.onSubmit}>
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <div>
          <label class="label" for="smoke-proof-kind">
            Proof kind
          </label>
          <select
            id="smoke-proof-kind"
            class="select"
            value={props.form.proof_kind}
            onChange={(e) => props.onField("proof_kind", e.currentTarget.value)}
          >
            <option value={NOCTANT_DOGFOOD_APP_LOOP_PROOF_KIND}>
              live app loop
            </option>
            <option value={NOCTANT_HISTORICAL_REPLAY_LAUNCH_PROOF_KIND}>
              historical replay launch
            </option>
            <option value={NOCTANT_HISTORICAL_BACKTEST_LAUNCH_PROOF_KIND}>
              historical backtest launch
            </option>
          </select>
        </div>
        <div>
          <label class="label" for="smoke-proof-status">
            Status
          </label>
          <select
            id="smoke-proof-status"
            class="select"
            value={props.form.status}
            onChange={(e) => props.onField("status", e.currentTarget.value)}
          >
            <option value="passed">passed</option>
            <option value="failed">failed</option>
          </select>
        </div>
        <SmokeProofInput
          field="runner"
          label="Runner"
          value={props.form.runner}
          placeholder="dogfood_app_loop"
          onField={props.onField}
        />
        <SmokeProofInput
          field="ran_at"
          label="Ran at"
          value={props.form.ran_at}
          placeholder="2026-06-18T12:00:00Z"
          onField={props.onField}
        />
        <SmokeProofInput
          field="source_git_commit"
          label="Source commit"
          value={props.form.source_git_commit}
          placeholder="0123456789abcdef0123456789abcdef01234567"
          onField={props.onField}
        />
        <SmokeProofInput
          field="source_git_head"
          label="Source head"
          value={props.form.source_git_head}
          placeholder="0123456789ab"
          onField={props.onField}
        />
        <SmokeProofInput
          field="account_id"
          label="Account"
          value={props.form.account_id}
          placeholder="00000000-0000-4000-8000-000000000000"
          onField={props.onField}
        />
        <SmokeProofInput
          field="launch_session_id"
          label="Launch session"
          value={props.form.launch_session_id}
          placeholder="00000000-0000-4000-8000-000000000000"
          onField={props.onField}
        />
        <SmokeProofInput
          field="webhook_event_id"
          label="Webhook event"
          value={props.form.webhook_event_id}
          placeholder="00000000-0000-4000-8000-000000000000"
          onField={props.onField}
        />
        <SmokeProofInput
          field="webhook_delivery_row_id"
          label="Delivery row"
          value={props.form.webhook_delivery_row_id}
          placeholder="optional"
          onField={props.onField}
        />
        <SmokeProofInput
          field="replay_request_id"
          label="Replay request"
          value={props.form.replay_request_id}
          placeholder="optional"
          onField={props.onField}
        />
        <div>
          <label class="label" for="smoke-proof-historical-mode">
            Historical mode
          </label>
          <select
            id="smoke-proof-historical-mode"
            class="select"
            value={props.form.historical_product_mode}
            onChange={(e) =>
              props.onField("historical_product_mode", e.currentTarget.value)
            }
          >
            <option value="">not historical</option>
            <option value="playback">playback</option>
            <option value="backtest">backtest</option>
          </select>
        </div>
        <SmokeProofInput
          field="historical_session_id"
          label="Historical session"
          value={props.form.historical_session_id}
          placeholder="optional"
          onField={props.onField}
        />
        <SmokeProofInput
          field="backtest_run_id"
          label="Backtest run"
          value={props.form.backtest_run_id}
          placeholder="optional"
          onField={props.onField}
        />
      </div>
      <SaveState error={props.error} saved={props.saved} />
      <button type="submit" class="btn-primary" disabled={props.recording}>
        {props.recording ? "Recording…" : "Record proof"}
      </button>
    </form>
  );
}

function SmokeProofInput(props: {
  field: SmokeProofFormField;
  label: string;
  value: string;
  placeholder: string;
  onField: (field: SmokeProofFormField, value: string) => void;
}) {
  return (
    <div>
      <label class="label" for={`smoke-proof-${props.field}`}>
        {props.label}
      </label>
      <input
        id={`smoke-proof-${props.field}`}
        class="input font-mono text-xs"
        value={props.value}
        placeholder={props.placeholder}
        spellcheck={false}
        onInput={(e) => props.onField(props.field, e.currentTarget.value)}
      />
    </div>
  );
}

function ReadinessReport(props: { report: OperatorApiReadinessReport }) {
  return (
    <div class="space-y-4">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field
          label="Status"
          value={
            <span class={readinessClass(props.report.ready)}>
              {props.report.ready ? "ready" : "not ready"}
            </span>
          }
        />
        <Field label="Operator status" value={props.report.status} />
      </div>
      <FindingsList
        title="Blockers"
        empty="No blockers."
        findings={props.report.blockers}
        tone="blocker"
      />
      <WebhookActivity activity={props.report.webhook_activity} />
      <SmokeProofActivity proof={props.report.last_smoke_proof} />
      <OnboardingSignoffActivity
        signoff={props.report.last_onboarding_signoff}
      />
      <FindingsList
        title="Warnings"
        empty="No warnings."
        findings={props.report.warnings}
        tone="warning"
      />
    </div>
  );
}

function SmokeProofActivity(props: {
  proof: OperatorApiReadinessReport["last_smoke_proof"];
}) {
  return (
    <div>
      <div class="text-xs uppercase text-slate-500 font-medium mb-2">
        Smoke proof
      </div>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Field
          label="Last proof"
          value={
            <Show when={props.proof} fallback="none">
              {(proof) => (
                <span class={smokeProofStatusClass(proof().status)}>
                  {proof().status}
                </span>
              )}
            </Show>
          }
        />
        <Field
          label="Runner"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().runner}
            </Show>
          }
          mono
        />
        <Field
          label="Ran at"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => formatTimestamp(proof().ran_at)}
            </Show>
          }
        />
        <Field
          label="Source"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().source_git_head}
            </Show>
          }
          mono
        />
        <Field
          label="Kind"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().proof_kind}
            </Show>
          }
          mono
        />
        <Field
          label="Historical mode"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().historical_product_mode ?? "—"}
            </Show>
          }
          mono
        />
        <Field
          label="Account"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().account_id ?? "accountless"}
            </Show>
          }
          mono
        />
        <Field
          label="Historical session"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().historical_session_id ?? "—"}
            </Show>
          }
          mono
        />
        <Field
          label="Backtest run"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().backtest_run_id ?? "—"}
            </Show>
          }
          mono
        />
        <Field
          label="Launch session"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => proof().launch_session_id}
            </Show>
          }
          mono
        />
        <Field
          label="Webhook evidence"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) =>
                proof().webhook_delivery_row_id ??
                proof().webhook_event_id ??
                proof().replay_request_id ??
                "—"
              }
            </Show>
          }
          mono
        />
        <Field
          label="Recorded"
          value={
            <Show when={props.proof} fallback="—">
              {(proof) => formatTimestamp(proof().recorded_at)}
            </Show>
          }
        />
      </div>
    </div>
  );
}

function OnboardingSignoffActivity(props: {
  signoff: OperatorApiReadinessReport["last_onboarding_signoff"];
}) {
  return (
    <div>
      <div class="text-xs uppercase text-slate-500 font-medium mb-2">
        Onboarding signoff
      </div>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Field
          label="Last signoff"
          value={
            <Show when={props.signoff} fallback="none">
              {(signoff) => (
                <span class={signoffStatusClass(signoff().status)}>
                  {signoff().status}
                </span>
              )}
            </Show>
          }
        />
        <Field
          label="Owner"
          value={
            <Show when={props.signoff} fallback="—">
              {(signoff) => signoff().owner}
            </Show>
          }
          mono
        />
        <Field
          label="Approved at"
          value={
            <Show when={props.signoff} fallback="—">
              {(signoff) => formatTimestamp(signoff().approved_at)}
            </Show>
          }
        />
        <Field
          label="Readiness"
          value={
            <Show when={props.signoff} fallback="—">
              {(signoff) => (signoff().readiness_ready ? "ready" : "not ready")}
            </Show>
          }
        />
        <Field
          label="Smoke proof"
          value={
            <Show when={props.signoff} fallback="—">
              {(signoff) => signoff().smoke_proof_id}
            </Show>
          }
          mono
        />
        <Field
          label="Limitations"
          value={
            <Show when={props.signoff} fallback="—">
              {(signoff) =>
                signoff().known_limitations.length
                  ? signoff().known_limitations.join("; ")
                  : "none"
              }
            </Show>
          }
        />
        <Field
          label="Recorded"
          value={
            <Show when={props.signoff} fallback="—">
              {(signoff) => formatTimestamp(signoff().recorded_at)}
            </Show>
          }
        />
      </div>
    </div>
  );
}

function WebhookActivity(props: {
  activity: OperatorApiReadinessReport["webhook_activity"];
}) {
  const delivery = () => props.activity.last_delivery;
  const replay = () => props.activity.last_replay;

  return (
    <div>
      <div class="text-xs uppercase text-slate-500 font-medium mb-2">
        Webhook activity
      </div>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Field
          label="Last delivery"
          value={
            <Show when={delivery()} fallback="none">
              {(item) => (
                <span class={webhookDeliveryStatusClass(item().status)}>
                  {webhookDeliveryStatusLabel(item().status)}
                </span>
              )}
            </Show>
          }
        />
        <Field
          label="Delivery event"
          value={
            <Show when={delivery()} fallback="—">
              {(item) => `${item().event_type} #${item().attempt_number}`}
            </Show>
          }
          mono
        />
        <Field
          label="Delivery finished"
          value={
            <Show when={delivery()} fallback="—">
              {(item) => formatTimestamp(item().finished_at)}
            </Show>
          }
        />
        <Field
          label="HTTP response"
          value={
            <Show when={delivery()} fallback="—">
              {(item) =>
                item().response_status?.toString() ??
                item().error_class ??
                "—"
              }
            </Show>
          }
          mono
        />
        <Field
          label="Last replay"
          value={
            <Show when={replay()} fallback="none">
              {(item) =>
                `${item().enqueued_count} / ${item().requested_limit} enqueued`
              }
            </Show>
          }
        />
        <Field
          label="Replay events"
          value={
            <Show when={replay()} fallback="—">
              {(item) => formatWebhookEventTypes(item().event_types)}
            </Show>
          }
          mono
        />
        <Field
          label="Replay window"
          value={
            <Show when={replay()} fallback="—">
              {(item) =>
                `${formatTimestamp(item().from_created_at)} to ${formatTimestamp(
                  item().to_created_at,
                )}`
              }
            </Show>
          }
          mono
        />
        <Field
          label="Replay request"
          value={
            <Show when={replay()} fallback="—">
              {(item) => item().request_id ?? item().replay_request_id}
            </Show>
          }
          mono
        />
      </div>
    </div>
  );
}

function FindingsList(props: {
  title: string;
  empty: string;
  findings: OperatorApiReadinessFinding[];
  tone: "blocker" | "warning";
}) {
  return (
    <div>
      <div class="text-xs uppercase text-slate-500 font-medium mb-2">
        {props.title}
      </div>
      <Show
        when={props.findings.length > 0}
        fallback={<p class="text-sm text-slate-600">{props.empty}</p>}
      >
        <ul class="space-y-2">
          <For each={props.findings}>
            {(finding) => (
              <li class={findingClass(props.tone)}>
                <div class="font-mono text-xs">{finding.code}</div>
                <div class="text-sm">{finding.message}</div>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}

function Field(props: {
  label: string;
  value: JSX.Element;
  mono?: boolean;
}) {
  return (
    <div class="text-sm min-w-0">
      <div class="text-xs text-slate-500">{props.label}</div>
      <div
        classList={{
          "mt-1 wrap-break-word": true,
          "font-mono text-xs": !!props.mono,
        }}
      >
        {props.value}
      </div>
    </div>
  );
}

function readinessClass(ready: boolean): string {
  return ready ? "status-active" : "status-pending";
}

function smokeProofStatusClass(status: string): string {
  return status === "passed" ? "status-active" : "status-suspended";
}

function signoffStatusClass(status: string): string {
  return status === "approved" ? "status-active" : "status-pending";
}

function webhookDeliveryStatusLabel(status: WebhookDeliveryStatus): string {
  switch (status) {
    case "delivered":
      return "delivered";
    case "transient_fail":
      return "retrying";
    case "permanent_fail":
      return "permanent fail";
    case "dead_letter":
      return "dead letter";
  }
}

function webhookDeliveryStatusClass(status: WebhookDeliveryStatus): string {
  switch (status) {
    case "delivered":
      return "status-active";
    case "transient_fail":
      return "status-pending";
    case "permanent_fail":
    case "dead_letter":
      return "status-suspended";
  }
}

function findingClass(tone: "blocker" | "warning"): string {
  return tone === "blocker"
    ? "rounded-sm border border-red-200 bg-red-50 p-2 text-red-800"
    : "rounded-sm border border-amber-200 bg-amber-50 p-2 text-amber-900";
}

function statusClass(status: string): string {
  switch (status) {
    case "active":
      return "status-active";
    case "pending":
      return "status-pending";
    case "suspended":
      return "status-suspended";
    default:
      return "status-neutral";
  }
}

